import { z } from 'zod';
import { consentSchema, healthAlertSchema, healthProfileViewSchema } from './kids.js';

/**
 * A operação do culto no Ministério Infantil (M19.3 a M19.7): a porta, a sala,
 * o diário e o chamado ao responsável.
 *
 * Aqui a regra que mais importa não é de dado, é de tempo: **menos de 20
 * segundos por família** na entrada, e nada de perder registro quando a
 * internet da igreja cai no meio do culto.
 */

// ── Estação de check-in ──────────────────────────────────────────────────────

/** Uma família na busca da porta: as crianças e quem pode entregar. */
export const familiaNaEstacaoSchema = z.object({
  guardianId: z.string().uuid(),
  guardianName: z.string(),
  guardianPhone: z.string().nullable(),
  relationship: z.string(),
  children: z.array(
    z.object({
      childId: z.string().uuid(),
      fullName: z.string(),
      nickname: z.string().nullable(),
      age: z.number().int(),
      classroomId: z.string().uuid().nullable(),
      classroomName: z.string().nullable(),
      photoUrl: z.string().nullable(),
      /** Só para quem pode ver saúde. Vazio para a recepção. */
      healthAlerts: z.array(healthAlertSchema),
      /** Já está na sala neste culto (ou em outro): não entra duas vezes. */
      checkedIn: z.boolean(),
      /** Código de retirada, quando já entrou. */
      code: z.string().nullable(),
      /** A liderança precisa ser chamada antes de entregar. */
      hasRestriction: z.boolean(),
      /** Ficha sem revisão há mais de 12 meses. */
      needsHealthReview: z.boolean(),
      /** Consentimentos que faltam, pelo nome do tipo. */
      missingConsents: z.array(z.string()),
    }),
  ),
});
export type FamiliaNaEstacao = z.infer<typeof familiaNaEstacaoSchema>;

export const stationQuerySchema = z.object({
  eventId: z.string().uuid(),
  /** Nome da criança, nome do responsável ou os 4 últimos dígitos do celular. */
  search: z.string().trim().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type StationQuery = z.infer<typeof stationQuerySchema>;

// ── Check-in ─────────────────────────────────────────────────────────────────

export const checkinChildSchema = z.object({
  /**
   * Id da operação, gerado pela estação **antes** de tentar a rede. É ele que
   * garante que o check-in feito offline não vira dois quando a rede volta.
   */
  operationId: z.string().uuid('A estação precisa identificar a operação'),
  childId: z.string().uuid(),
  eventId: z.string().uuid(),
  /** Quem entregou a criança na porta. */
  deliveredByGuardianId: z.string().uuid().nullish(),
  deliveredByName: z.string().trim().min(3, 'Diga quem entregou a criança').max(160),
  notes: z.string().trim().max(500).nullish(),
  /** Quando a estação registrou, para o offline não mentir a hora. */
  checkedInAt: z.string().datetime().optional(),
});
export type CheckinChildInput = z.infer<typeof checkinChildSchema>;

/** A família inteira em uma operação (cenário 22): irmãos, um toque. */
export const checkinFamilySchema = z.object({
  eventId: z.string().uuid(),
  deliveredByGuardianId: z.string().uuid().nullish(),
  deliveredByName: z.string().trim().min(3, 'Diga quem entregou as crianças').max(160),
  children: z
    .array(z.object({ childId: z.string().uuid(), operationId: z.string().uuid() }))
    .min(1, 'Escolha pelo menos uma criança')
    .max(10),
  checkedInAt: z.string().datetime().optional(),
});
export type CheckinFamilyInput = z.infer<typeof checkinFamilySchema>;

export const CHECKIN_WARNINGS = [
  'sala_cheia',
  'proporcao_estourada',
  'restricao_legal',
  'ficha_desatualizada',
  'sem_consentimento',
  'sem_sala_para_a_idade',
] as const;
export const checkinWarningSchema = z.enum(CHECKIN_WARNINGS);
export type CheckinWarning = z.infer<typeof checkinWarningSchema>;

export const CHECKIN_WARNING_LABEL: Record<CheckinWarning, string> = {
  sala_cheia: 'A sala chegou à lotação',
  proporcao_estourada: 'Faltam adultos para a quantidade de crianças',
  restricao_legal: 'Esta criança tem restrição legal — chame a liderança',
  ficha_desatualizada: 'A ficha de saúde não é revisada há mais de um ano',
  sem_consentimento: 'Falta consentimento do responsável',
  sem_sala_para_a_idade: 'Nenhuma sala cobre a idade desta criança',
};

export const checkinSchema = z.object({
  id: z.string().uuid(),
  childId: z.string().uuid(),
  childName: z.string(),
  childNickname: z.string().nullable(),
  eventId: z.string().uuid(),
  eventTitle: z.string(),
  classroomId: z.string().uuid().nullable(),
  classroomName: z.string().nullable(),
  code: z.string(),
  checkedInAt: z.string().datetime(),
  deliveredByName: z.string(),
  checkedOutAt: z.string().datetime().nullable(),
  pickedUpByName: z.string().nullable(),
  isException: z.boolean(),
  exceptionReason: z.string().nullable(),
  /** Só para quem pode ver saúde — é o que vai na etiqueta. */
  healthAlerts: z.array(healthAlertSchema),
  /** O que a estação avisou na hora de registrar. */
  warnings: z.array(checkinWarningSchema),
});
export type Checkin = z.infer<typeof checkinSchema>;

export const checkinResultSchema = z.object({
  items: z.array(checkinSchema),
  /** Operações que já tinham sido aplicadas antes (a rede voltou duas vezes). */
  repetidas: z.number().int(),
});
export type CheckinResult = z.infer<typeof checkinResultSchema>;

// ── Check-out ────────────────────────────────────────────────────────────────

export const checkoutSchema = z.object({
  code: z.string().trim().min(4, 'Informe o código de retirada').max(12),
  /** Quem está levando. Precisa estar na lista de autorizados. */
  pickedUpById: z.string().uuid('Escolha quem está retirando'),
  eventId: z.string().uuid(),
});
export type CheckoutInput = z.infer<typeof checkoutSchema>;

export const EXCEPTION_KINDS = ['LOST_CODE', 'NOT_ON_LIST', 'BOTH'] as const;
export const exceptionKindSchema = z.enum(EXCEPTION_KINDS);
export type ExceptionKind = z.infer<typeof exceptionKindSchema>;

export const EXCEPTION_KIND_LABEL: Record<ExceptionKind, string> = {
  LOST_CODE: 'O responsável perdeu o código',
  NOT_ON_LIST: 'Quem veio buscar não está na lista',
  BOTH: 'Sem código e fora da lista',
};

/** Retirada excepcional (M19.4): exige líder, motivo e registro. */
export const exceptionCheckoutSchema = z.object({
  checkinId: z.string().uuid(),
  kind: exceptionKindSchema,
  reason: z
    .string()
    .trim()
    .min(15, 'Escreva o que aconteceu: este registro fica na auditoria')
    .max(500),
  pickedUpByName: z.string().trim().min(3, 'Informe quem está retirando').max(160),
  /** Confirmação do responsável principal, quando foi possível obter. */
  guardianConfirmed: z.boolean().default(false),
});
export type ExceptionCheckoutInput = z.infer<typeof exceptionCheckoutSchema>;

export const CHECKOUT_REFUSALS = [
  'codigo_invalido',
  'nao_autorizado',
  'ja_retirada',
  'crianca_de_outro_culto',
] as const;
export const checkoutRefusalSchema = z.enum(CHECKOUT_REFUSALS);
export type CheckoutRefusal = z.infer<typeof checkoutRefusalSchema>;

export const CHECKOUT_REFUSAL_LABEL: Record<CheckoutRefusal, string> = {
  codigo_invalido: 'Este código não vale para nenhuma criança na sala agora.',
  nao_autorizado: 'Quem está aqui não está na lista de quem pode retirar esta criança.',
  ja_retirada: 'Esta criança já foi retirada.',
  crianca_de_outro_culto: 'O código é de outro culto.',
};

// ── Sala ao vivo ─────────────────────────────────────────────────────────────

export const salaAoVivoSchema = z.object({
  classroomId: z.string().uuid(),
  classroomName: z.string(),
  color: z.string(),
  childrenPerAdult: z.number().int(),
  capacity: z.number().int(),
  /** Adultos escalados e presentes nesta sala neste culto. */
  adultsCount: z.number().int(),
  adults: z.array(z.object({ personId: z.string().uuid(), name: z.string() })),
  children: z.array(
    z.object({
      checkinId: z.string().uuid(),
      childId: z.string().uuid(),
      fullName: z.string(),
      nickname: z.string().nullable(),
      photoUrl: z.string().nullable(),
      code: z.string(),
      checkedInAt: z.string().datetime(),
      healthAlerts: z.array(healthAlertSchema),
      /** Chamado ao responsável em aberto. */
      callingGuardian: z.boolean(),
    }),
  ),
  /** Falta adulto para a quantidade de crianças. */
  ratioBreached: z.boolean(),
  /** A sala está com um adulto só (regra dos dois adultos, M19.8). */
  aloneAlert: z.boolean(),
  /** Quantos adultos a sala precisa agora. */
  adultsNeeded: z.number().int(),
});
export type SalaAoVivo = z.infer<typeof salaAoVivoSchema>;

export const painelDoKidsSchema = z.object({
  eventId: z.string().uuid(),
  eventTitle: z.string(),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  rooms: z.array(salaAoVivoSchema),
  /** Crianças ainda na sala depois do fim do culto, com o tempo decorrido. */
  pending: z.array(
    z.object({
      checkinId: z.string().uuid(),
      childId: z.string().uuid(),
      fullName: z.string(),
      classroomName: z.string().nullable(),
      code: z.string(),
      minutosDesdeOFim: z.number().int(),
      guardianName: z.string().nullable(),
      guardianPhone: z.string().nullable(),
    }),
  ),
  totalPresentes: z.number().int(),
});
export type PainelDoKids = z.infer<typeof painelDoKidsSchema>;

// ── Diário e ocorrências ─────────────────────────────────────────────────────

export const INCIDENT_KINDS = [
  'CRYING',
  'ILLNESS',
  'FEVER',
  'VOMIT',
  'FALL',
  'CONFLICT',
  'ATYPICAL_BEHAVIOR',
  'ALLERGY_REACTION',
  'MEDICATION_GIVEN',
  'DIAPER',
  'FOOD',
  'SLEEP',
  'BATHROOM',
  'EARLY_LEAVE',
  'POSITIVE',
  'OTHER',
] as const;
export const incidentKindSchema = z.enum(INCIDENT_KINDS);
export type IncidentKind = z.infer<typeof incidentKindSchema>;

export const INCIDENT_KIND_LABEL: Record<IncidentKind, string> = {
  CRYING: 'Choro persistente',
  ILLNESS: 'Mal-estar',
  FEVER: 'Febre',
  VOMIT: 'Vômito',
  FALL: 'Queda ou machucado',
  CONFLICT: 'Conflito com outra criança',
  ATYPICAL_BEHAVIOR: 'Comportamento atípico',
  ALLERGY_REACTION: 'Reação alérgica',
  MEDICATION_GIVEN: 'Medicamento administrado',
  DIAPER: 'Troca de fralda',
  FOOD: 'Alimentação',
  SLEEP: 'Sono',
  BATHROOM: 'Banheiro',
  EARLY_LEAVE: 'Saída antecipada',
  POSITIVE: 'Registro positivo',
  OTHER: 'Outro',
};

export const INCIDENT_SEVERITIES = ['INFO', 'ATTENTION', 'URGENT'] as const;
export const incidentSeveritySchema = z.enum(INCIDENT_SEVERITIES);
export type IncidentSeverity = z.infer<typeof incidentSeveritySchema>;

export const INCIDENT_SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  INFO: 'Informativa',
  ATTENTION: 'Atenção',
  URGENT: 'Urgente',
};

export const INCIDENT_SEVERITY_AJUDA: Record<IncidentSeverity, string> = {
  INFO: 'Entra no resumo que o responsável recebe no fim do culto.',
  ATTENTION: 'Avisa o responsável na hora, por push e WhatsApp.',
  URGENT: 'Avisa na hora e, sem resposta, chama o segundo responsável e a liderança.',
};

export const createIncidentSchema = z.object({
  childId: z.string().uuid(),
  checkinId: z.string().uuid().nullish(),
  kind: incidentKindSchema,
  severity: incidentSeveritySchema.default('INFO'),
  description: z.string().trim().min(5, 'Conte o que aconteceu').max(2000),
  action: z.string().trim().max(1000).nullish(),
  fileId: z.string().uuid().nullish(),
});
export type CreateIncidentInput = z.infer<typeof createIncidentSchema>;

export const incidentSchema = z.object({
  id: z.string().uuid(),
  childId: z.string().uuid(),
  childName: z.string(),
  kind: incidentKindSchema,
  severity: incidentSeveritySchema,
  description: z.string(),
  action: z.string().nullable(),
  fileId: z.string().uuid().nullable(),
  authorName: z.string().nullable(),
  notifiedAt: z.string().datetime().nullable(),
  acknowledgedAt: z.string().datetime().nullable(),
  escalatedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type Incident = z.infer<typeof incidentSchema>;

/** Quantos minutos uma ocorrência urgente espera antes de subir um degrau. */
export const MINUTOS_ATE_ESCALONAR = 10;

// ── Chamar o responsável ─────────────────────────────────────────────────────

export const GUARDIAN_CALL_REASONS = [
  'CRYING',
  'NEEDS_YOU',
  'ILLNESS',
  'DIAPER',
  'URGENT',
] as const;
export const guardianCallReasonSchema = z.enum(GUARDIAN_CALL_REASONS);
export type GuardianCallReason = z.infer<typeof guardianCallReasonSchema>;

export const GUARDIAN_CALL_REASON_LABEL: Record<GuardianCallReason, string> = {
  CRYING: 'Está chorando',
  NEEDS_YOU: 'Precisa de você',
  ILLNESS: 'Mal-estar',
  DIAPER: 'Troca de fralda',
  URGENT: 'Urgente',
};

/** O texto que chega ao responsável. Acolhedor: ninguém quer susto no culto. */
export const GUARDIAN_CALL_MENSAGEM: Record<GuardianCallReason, string> = {
  CRYING: 'O {crianca} está chorando e pede por você. Pode vir até a sala {sala}?',
  NEEDS_YOU: 'O {crianca} está precisando de você na sala {sala}.',
  ILLNESS: 'O {crianca} não está se sentindo bem. Venha até a sala {sala}, por favor.',
  DIAPER: 'Precisamos de você na sala {sala} para uma troca do {crianca}.',
  URGENT: 'Precisamos de você agora na sala {sala}, com o {crianca}.',
};

export const createGuardianCallSchema = z.object({
  childId: z.string().uuid(),
  checkinId: z.string().uuid().nullish(),
  reason: guardianCallReasonSchema,
  message: z.string().trim().max(500).nullish(),
});
export type CreateGuardianCallInput = z.infer<typeof createGuardianCallSchema>;

export const GUARDIAN_CALL_STATUS = ['SENT', 'ACKNOWLEDGED', 'ARRIVED', 'CANCELLED'] as const;
export const guardianCallStatusSchema = z.enum(GUARDIAN_CALL_STATUS);
export type GuardianCallStatus = z.infer<typeof guardianCallStatusSchema>;

export const GUARDIAN_CALL_STATUS_LABEL: Record<GuardianCallStatus, string> = {
  SENT: 'Chamado enviado',
  ACKNOWLEDGED: 'O responsável viu',
  ARRIVED: 'O responsável chegou',
  CANCELLED: 'Chamado cancelado',
};

export const guardianCallSchema = z.object({
  id: z.string().uuid(),
  childId: z.string().uuid(),
  childName: z.string(),
  reason: guardianCallReasonSchema,
  message: z.string().nullable(),
  status: guardianCallStatusSchema,
  createdAt: z.string().datetime(),
  acknowledgedAt: z.string().datetime().nullable(),
  arrivedAt: z.string().datetime().nullable(),
  /** Há quantos segundos o chamado está de pé. */
  segundosEsperando: z.number().int(),
});
export type GuardianCall = z.infer<typeof guardianCallSchema>;

// ── Visão do responsável (M19.9) ─────────────────────────────────────────────

export const meuFilhoSchema = z.object({
  childId: z.string().uuid(),
  fullName: z.string(),
  nickname: z.string().nullable(),
  photoUrl: z.string().nullable(),
  classroomName: z.string().nullable(),
  /** Está na sala agora? Desde quando? */
  presente: z.boolean(),
  checkedInAt: z.string().datetime().nullable(),
  code: z.string().nullable(),
  eventTitle: z.string().nullable(),
  /** Ocorrências deste culto que ainda não foram confirmadas. */
  avisosNaoLidos: z.number().int(),
});
export type MeuFilho = z.infer<typeof meuFilhoSchema>;

export const resumoDoCultoSchema = z.object({
  childId: z.string().uuid(),
  childName: z.string(),
  eventTitle: z.string(),
  checkedInAt: z.string().datetime(),
  checkedOutAt: z.string().datetime().nullable(),
  /** O que a criança fez, comeu e aprendeu. */
  incidents: z.array(incidentSchema),
  /** Frase amigável montada a partir do diário. */
  resumo: z.string(),
});
export type ResumoDoCulto = z.infer<typeof resumoDoCultoSchema>;

// ── Lista de emergência (M19.10) ─────────────────────────────────────────────

export const linhaDeEmergenciaSchema = z.object({
  childName: z.string(),
  classroomName: z.string().nullable(),
  code: z.string(),
  guardianName: z.string().nullable(),
  guardianPhone: z.string().nullable(),
  /** Só o essencial, e só para quem pode ver saúde. */
  alerts: z.array(z.string()),
});
export type LinhaDeEmergencia = z.infer<typeof linhaDeEmergenciaSchema>;

// ── Exportar e apagar os dados da criança (LGPD art. 18) ─────────────────────

/**
 * O responsável pede que os dados do filho sejam apagados. É direito dele, e o
 * produto obedece — guardando só o que a igreja precisa manter para provar o
 * cuidado que já prestou.
 */
export const erasureRequestSchema = z.object({
  reason: z.string().trim().max(300).nullish(),
  /** Confirmação explícita: apagar não tem volta. */
  confirmed: z.literal(true, {
    errorMap: () => ({ message: 'Confirme que entende que os dados serão apagados' }),
  }),
});
export type ErasureRequestInput = z.infer<typeof erasureRequestSchema>;

export const erasureResultSchema = z.object({
  childId: z.string().uuid(),
  /** O que saiu do banco de vez. */
  apagados: z.array(z.string()),
  /** O que ficou, e por quê. */
  mantidos: z.array(z.object({ o_que: z.string(), porque: z.string() })),
});
export type ErasureResult = z.infer<typeof erasureResultSchema>;

/** Tudo o que a igreja guarda sobre a criança, para o responsável levar. */
export const childExportSchema = z.object({
  crianca: z.object({
    fullName: z.string(),
    nickname: z.string().nullable(),
    birthDate: z.string(),
    classroomName: z.string().nullable(),
    status: z.string(),
  }),
  responsaveis: z.array(
    z.object({ name: z.string(), relationship: z.string(), phone: z.string().nullable() }),
  ),
  saude: healthProfileViewSchema.nullable(),
  consentimentos: z.array(consentSchema),
  presencas: z.array(
    z.object({
      evento: z.string(),
      entrada: z.string().datetime(),
      saida: z.string().datetime().nullable(),
      retiradaPor: z.string().nullable(),
    }),
  ),
  ocorrencias: z.array(incidentSchema),
  geradoEm: z.string().datetime(),
});
export type ChildExport = z.infer<typeof childExportSchema>;
